/**
 * useDiscovery (CHAP-593): the Discovery admin API, through the app's own
 * fetch helper, so every POST carries the session cookie and the X-CSRF-Token
 * header like the rest of the app.
 *
 * Routes (the plan's admin contract; the server enforces session, CSRF, the
 * admin role, DISCOVERY_SENDERS and no impersonation):
 *   GET  /api/c/:tenant/discovery/status
 *   POST /api/c/:tenant/discovery/{import,gates,seed,prepare,release,cancel,stop,mark-replied,retry-notices}
 *   GET  /api/c/:tenant/discovery/preview?emailNo=&variant=
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, ApiError } from '../lib/api.js';
import {
  createMarkLock,
  normaliseStatus,
  readCopySha,
  type DiscoveryStatus,
  type EmailNo,
  type MarkLock,
  type Variant,
} from '../pages/discovery/model.js';

export function discoveryBase(slug: string): string {
  return `/api/c/${encodeURIComponent(slug)}/discovery`;
}

const statusKey = (slug: string) => ['discovery-status', slug] as const;

export function useDiscoveryStatus(slug: string) {
  return useQuery<DiscoveryStatus, Error>({
    queryKey: statusKey(slug),
    queryFn: async () => normaliseStatus(await apiGet<unknown>(`${discoveryBase(slug)}/status`)),
    enabled: !!slug,
    // A 403 is an answer, not a blip: show it at once.
    retry: false,
    // While a batch sends, the counts move; keep them current when the tab is open.
    refetchInterval: 15_000,
  });
}

/** The POST actions whose results the page keeps in mutation state. Import is not one: see postImport. */
export type DiscoveryAction =
  | 'gates'
  | 'seed'
  | 'prepare'
  | 'release'
  | 'cancel'
  | 'stop'
  | 'mark-replied'
  | 'retry-notices';

/**
 * A POST action. Its onSettled returns the status invalidation, and TanStack
 * Query 5 awaits it before the mutation settles; invalidateQueries resolves
 * once the active status query has fetched again (any fetch already running is
 * cancelled first). So a mutateAsync promise settles only after the page holds
 * a status read after the write, which is what the Mark checked lock waits for.
 */
export function useDiscoveryAction<TBody = void, TResult = unknown>(slug: string, action: DiscoveryAction) {
  const qc = useQueryClient();
  return useMutation<TResult, Error, TBody>({
    mutationFn: (body: TBody) => apiPost<TResult>(`${discoveryBase(slug)}/${action}`, body ?? undefined),
    onSettled: () => qc.invalidateQueries({ queryKey: statusKey(slug) }),
  });
}

/**
 * The page's one Mark checked lock (model.ts MarkLock), shared by the
 * Dispatches rows and the seed gates, and its held state for rendering.
 */
export function useSeedMarkLock(): { lock: MarkLock; held: boolean } {
  const [held, setHeld] = useState(false);
  const ref = useRef<MarkLock | null>(null);
  if (ref.current === null) ref.current = createMarkLock(setHeld);
  return { lock: ref.current, held };
}

/**
 * The CSV import, outside React Query on purpose: a mutation keeps its
 * variables in the cache, and the variables here would be the whole list.
 * This sends it and returns only the server's response.
 */
export async function postImport(slug: string, csv: string): Promise<unknown> {
  return apiPost<unknown>(`${discoveryBase(slug)}/import`, { csv });
}

export function useRefreshDiscovery(slug: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: statusKey(slug) });
}

export interface PreviewResult {
  html: string;
  text: string | null;
  subject: string | null;
  copySha: string | null;
}

/**
 * GET the rendered email for a sample recipient. The plan says it "returns the
 * rendered HTML"; a JSON body of the renderer's { subject, html, text, copySha }
 * is read as well.
 */
export async function fetchDiscoveryPreview(slug: string, emailNo: EmailNo, variant: Variant): Promise<PreviewResult> {
  const qs = new URLSearchParams({ emailNo: String(emailNo), variant });
  const res = await fetch(`${discoveryBase(slug)}/preview?${qs.toString()}`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'text/html, application/json' },
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    throw new ApiError(res.status, body, `preview failed: ${res.status}`);
  }
  if ((res.headers.get('content-type') ?? '').includes('application/json')) {
    const data = (await res.json()) as Record<string, unknown>;
    return {
      html: typeof data.html === 'string' ? data.html : '',
      text: typeof data.text === 'string' ? data.text : null,
      subject: typeof data.subject === 'string' ? data.subject : null,
      copySha: readCopySha(data),
    };
  }
  return { html: await res.text(), text: null, subject: null, copySha: null };
}
